import React, { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../supabase";
import { C, font } from "../config/theme";

// ─── Labels des rôles en français ───
const ROLE_LABELS = {
  admin: "Administrateur",
  dirigeant: "Dirigeant",
  conducteur: "Conducteur",
  user: "Utilisateur",
};

// ─── Couleurs des badges de rôle ───
const ROLE_COLORS = {
  admin: { bg: "#EDE9FE", text: "#6D28D9" },
  dirigeant: { bg: "#DBEAFE", text: "#1D4ED8" },
  conducteur: { bg: "#FEF3C7", text: "#92400E" },
  user: { bg: "#F1F5F9", text: "#475569" },
};

/**
 * Page "Mon équipe" — gestion des utilisateurs du tenant
 *
 * Props :
 * - tenantId : UUID du tenant
 * - tenant   : objet tenant { id, nom, maxUsers, extraUsers }
 * - role     : rôle de l'utilisateur connecté
 * - profile  : profil de l'utilisateur connecté
 *
 * Fonctionnalités :
 * - Affiche la liste des membres avec rôle et statut
 * - Affiche le quota (utilisateurs actifs / quota max)
 * - Permet d'inviter un nouvel utilisateur (via Edge Function)
 * - Permet de modifier un utilisateur (nom, prénom, rôle) en édition inline
 * - Permet de désactiver/réactiver un utilisateur
 * - Permet de supprimer définitivement un utilisateur (via Edge Function)
 */
export default function PageEquipe({ tenantId, tenant, role, profile }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  // ─── Formulaire d'invitation ───
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteNom, setInviteNom] = useState("");
  const [invitePrenom, setInvitePrenom] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [inviting, setInviting] = useState(false);

  // ─── Édition d'un utilisateur ───
  const [editingId, setEditingId] = useState(null);
  const [editNom, setEditNom] = useState("");
  const [editPrenom, setEditPrenom] = useState("");
  const [editRole, setEditRole] = useState("");
  const [saving, setSaving] = useState(false);

  // ─── Suppression en cours (pour désactiver le bouton pendant l'appel) ───
  const [deletingId, setDeletingId] = useState(null);

  // Seuls admin et patron peuvent gérer l'équipe
  const canManage = role === "admin" || role === "dirigeant";

  // ─── Compteurs ───
  const activeCount = useMemo(() => users.filter((u) => u.actif).length, [users]);
  const quota = (tenant?.maxUsers || 1) + (tenant?.extraUsers || 0);
  const quotaReached = activeCount >= quota;

  // ─── Style commun pour les inputs ───
  const inputStyle = {
    width: "100%",
    padding: "8px 12px",
    fontSize: 13,
    fontFamily: font,
    border: "1px solid #E2E8F0",
    borderRadius: 6,
    outline: "none",
    color: C.text,
  };

  // ─── Charger la liste des utilisateurs ───
  const loadUsers = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    const { data, error } = await supabase.rpc("get_tenant_users", {
      p_tenant_id: tenantId,
    });
    if (error) {
      console.error("Erreur chargement équipe:", error);
      setFeedback({ type: "error", message: "Erreur de chargement de l'équipe" });
    }
    if (data) setUsers(data);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // ─── Inviter un utilisateur via Edge Function ───
  const handleInvite = useCallback(async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setFeedback(null);

    const { data, error } = await supabase.functions.invoke("invite-user", {
      body: {
        email: inviteEmail.trim(),
        nom: inviteNom.trim(),
        prenom: invitePrenom.trim(),
        role: inviteRole,
        tenant_id: tenantId,
      },
    });

    if (error) {
      setFeedback({ type: "error", message: error.message || "Erreur lors de l'invitation" });
    } else if (data?.error) {
      setFeedback({ type: "error", message: data.error });
    } else {
      setFeedback({
        type: "success",
        message: `Invitation envoyée à ${inviteEmail}${data?.invitation_sent ? "" : " (email non envoyé — le compte est créé)"}`,
      });
      setInviteEmail("");
      setInviteNom("");
      setInvitePrenom("");
      setInviteRole("user");
      setShowInvite(false);
      await loadUsers();
    }
    setInviting(false);
  }, [inviteEmail, inviteNom, invitePrenom, inviteRole, tenantId, loadUsers]);

  // ─── Supprimer définitivement un utilisateur via Edge Function ───
  const handleDelete = useCallback(
    async (userId, userName) => {
      // Double confirmation — action irréversible
      if (!window.confirm(`Supprimer définitivement ${userName} ?\n\nCette action est irréversible. Le compte et toutes ses données seront supprimés.`)) return;
      if (!window.confirm(`Confirmez-vous la suppression définitive de ${userName} ?`)) return;

      setDeletingId(userId);
      setFeedback(null);

      const { data, error } = await supabase.functions.invoke("delete-user", {
        body: { user_id: userId },
      });

      if (error) {
        setFeedback({ type: "error", message: error.message || "Erreur lors de la suppression" });
      } else if (data?.error) {
        setFeedback({ type: "error", message: data.error });
      } else {
        setFeedback({
          type: "success",
          message: `${userName} a été supprimé définitivement${data?.warning ? ` (⚠ ${data.warning})` : ""}`,
        });
        await loadUsers();
      }
      setDeletingId(null);
    },
    [loadUsers]
  );

  /**
   * Détermine si l'utilisateur connecté peut supprimer un user cible
   * - Admin : peut supprimer tout sauf lui-même
   * - Patron : peut supprimer user et conducteur de son tenant (pas patron, pas admin)
   */
  const canDelete = useCallback(
    (user) => {
      if (user.user_id === profile?.id) return false;
      if (role === "admin") return true;
      if (role === "dirigeant") return ["user", "conducteur"].includes(user.role);
      return false;
    },
    [role, profile]
  );

  // ─── Ouvrir l'édition d'un utilisateur ───
  const startEdit = useCallback((user) => {
    setEditingId(user.user_id);
    setEditNom(user.nom || "");
    setEditPrenom(user.prenom || "");
    setEditRole(user.role || "user");
  }, []);

  // ─── Annuler l'édition ───
  const cancelEdit = useCallback(() => {
    setEditingId(null);
  }, []);

  // ─── Sauvegarder les modifications ───
  const handleSaveEdit = useCallback(async () => {
    setSaving(true);
    setFeedback(null);

    const { error } = await supabase.rpc("update_user_profile", {
      p_tenant_id: tenantId,
      p_user_id: editingId,
      p_nom: editNom,
      p_prenom: editPrenom,
      p_role: editRole,
    });

    if (error) {
      setFeedback({ type: "error", message: error.message });
    } else {
      setFeedback({ type: "success", message: "Utilisateur modifié" });
      setEditingId(null);
      await loadUsers();
    }
    setSaving(false);
  }, [editingId, editNom, editPrenom, editRole, tenantId, loadUsers]);

  // ─── Chargement ───
  if (loading) {
    return (
      <div style={{ padding: 40, color: C.slate, fontSize: 14 }}>
        Chargement de l'équipe…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800 }}>
      {/* ─── En-tête ─── */}
      <div style={{ marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.navy, margin: 0 }}>
            Mon équipe
          </h1>
          <p style={{ fontSize: 13, color: C.slate, marginTop: 4 }}>
            {activeCount} / {quota} utilisateur{quota > 1 ? "s" : ""} actif{activeCount > 1 ? "s" : ""}
          </p>
        </div>

        {/* Bouton inviter */}
        {canManage && (
          <button
            onClick={() => setShowInvite(!showInvite)}
            disabled={quotaReached && !showInvite}
            style={{
              padding: "9px 20px",
              background: quotaReached ? "#CBD5E1" : C.navy,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              cursor: quotaReached ? "not-allowed" : "pointer",
              fontSize: 13,
              fontWeight: 700,
              fontFamily: font,
            }}
          >
            {showInvite ? "Annuler" : quotaReached ? "Quota atteint" : "+ Inviter"}
          </button>
        )}
      </div>

      {/* ─── Quota atteint ─── */}
      {quotaReached && canManage && (
        <div
          style={{
            padding: "10px 16px",
            background: "#FEF3C7",
            borderRadius: 8,
            fontSize: 13,
            color: "#92400E",
            marginBottom: 20,
          }}
        >
          Quota utilisateurs atteint ({activeCount}/{quota}). Contactez l'administrateur pour ajouter des accès supplémentaires.
        </div>
      )}

      {/* ─── Feedback ─── */}
      {feedback && (
        <div
          style={{
            padding: "10px 16px",
            background: feedback.type === "success" ? "#D1FAE5" : "#FEE2E2",
            borderRadius: 8,
            fontSize: 13,
            color: feedback.type === "success" ? "#065F46" : "#991B1B",
            marginBottom: 20,
          }}
        >
          {feedback.message}
        </div>
      )}

      {/* ─── Formulaire d'invitation ─── */}
      {showInvite && (
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            padding: 20,
            marginBottom: 20,
          }}
        >
          <h2 style={{ fontSize: 14, fontWeight: 700, color: C.navy, margin: "0 0 16px 0" }}>
            Inviter un utilisateur
          </h2>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: "block", marginBottom: 4 }}>
                Prénom
              </label>
              <input
                type="text"
                value={invitePrenom}
                onChange={(e) => setInvitePrenom(e.target.value)}
                placeholder="Jean"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: "block", marginBottom: 4 }}>
                Nom
              </label>
              <input
                type="text"
                value={inviteNom}
                onChange={(e) => setInviteNom(e.target.value)}
                placeholder="Dupont"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: "block", marginBottom: 4 }}>
                Email *
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="jean.dupont@entreprise.fr"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.slate, display: "block", marginBottom: 4 }}>
                Rôle
              </label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                <option value="user">Utilisateur</option>
                <option value="dirigeant">Dirigeant</option>
                <option value="conducteur">Conducteur de chantier</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleInvite}
            disabled={!inviteEmail.trim() || inviting}
            style={{
              padding: "9px 24px",
              background: !inviteEmail.trim() ? "#CBD5E1" : C.navy,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              cursor: !inviteEmail.trim() ? "not-allowed" : "pointer",
              fontSize: 13,
              fontWeight: 700,
              fontFamily: font,
              opacity: inviting ? 0.7 : 1,
            }}
          >
            {inviting ? "Envoi en cours…" : "Envoyer l'invitation"}
          </button>
        </div>
      )}

      {/* ─── Liste des membres ─── */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {/* En-tête du tableau */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto auto",
            gap: 12,
            padding: "12px 20px",
            background: "#FAFBFC",
            borderBottom: "1px solid #F1F5F9",
            fontSize: 11,
            fontWeight: 700,
            color: C.slate,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          <div>Utilisateur</div>
          <div>Email</div>
          <div style={{ textAlign: "center" }}>Rôle</div>
          <div style={{ textAlign: "center", minWidth: 200 }}>Actions</div>
        </div>

        {/* Lignes */}
        {users.map((user, idx) => {
          const isMe = user.user_id === profile?.id;
          const roleColor = ROLE_COLORS[user.role] || ROLE_COLORS.user;
          const displayName = [user.prenom, user.nom].filter(Boolean).join(" ") || "—";
          const isDeleting = deletingId === user.user_id;

          return (
            <div
              key={user.user_id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr auto auto",
                gap: 12,
                padding: "12px 20px",
                borderBottom: idx < users.length - 1 ? "1px solid #F8FAFC" : "none",
                alignItems: "center",
                opacity: user.actif ? 1 : 0.5,
              }}
            >
              {/* Nom — mode édition ou affichage */}
              <div>
                {editingId === user.user_id ? (
                  <div style={{ display: "flex", gap: 6 }}>
                    <input
                      value={editPrenom}
                      onChange={(e) => setEditPrenom(e.target.value)}
                      placeholder="Prénom"
                      style={{ ...inputStyle, width: "45%", padding: "5px 8px", fontSize: 12 }}
                    />
                    <input
                      value={editNom}
                      onChange={(e) => setEditNom(e.target.value)}
                      placeholder="Nom"
                      style={{ ...inputStyle, width: "45%", padding: "5px 8px", fontSize: 12 }}
                    />
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                      {displayName}
                      {isMe && (
                        <span style={{ fontSize: 10, color: C.slate, marginLeft: 6 }}>(vous)</span>
                      )}
                    </div>
                    {!user.actif && (
                      <div style={{ fontSize: 11, color: "#DC2626", marginTop: 2 }}>Désactivé</div>
                    )}
                  </div>
                )}
              </div>

              {/* Email */}
              <div style={{ fontSize: 13, color: C.slate }}>{user.email || "—"}</div>

              {/* Badge rôle — éditable ou badge */}
              <div style={{ textAlign: "center" }}>
                {editingId === user.user_id ? (
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    style={{ ...inputStyle, width: "auto", padding: "4px 8px", fontSize: 11 }}
                  >
                    <option value="user">Utilisateur</option>
                    <option value="conducteur">Conducteur</option>
                    <option value="dirigeant">Dirigeant</option>
                    {role === "admin" && <option value="admin">Administrateur</option>}
                  </select>
                ) : (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "3px 10px",
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      background: roleColor.bg,
                      color: roleColor.text,
                    }}
                  >
                    {ROLE_LABELS[user.role] || user.role}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div style={{ textAlign: "center", minWidth: 200 }}>
                {editingId === user.user_id ? (
                  <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                    <button
                      onClick={handleSaveEdit}
                      disabled={saving}
                      style={{
                        padding: "4px 12px",
                        fontSize: 11,
                        fontWeight: 600,
                        fontFamily: font,
                        border: "none",
                        borderRadius: 6,
                        background: C.navy,
                        color: "#fff",
                        cursor: "pointer",
                        opacity: saving ? 0.7 : 1,
                      }}
                    >
                      {saving ? "…" : "OK"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      style={{
                        padding: "4px 10px",
                        fontSize: 11,
                        fontWeight: 600,
                        fontFamily: font,
                        border: "1px solid #E2E8F0",
                        borderRadius: 6,
                        background: "transparent",
                        color: C.slate,
                        cursor: "pointer",
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                ) : (
                  canManage && !isMe && (
                    <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                      <button
                        onClick={() => startEdit(user)}
                        style={{
                          padding: "4px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                          fontFamily: font,
                          border: "1px solid #E2E8F0",
                          borderRadius: 6,
                          background: "transparent",
                          color: C.navy,
                          cursor: "pointer",
                        }}
                      >
                        Modifier
                      </button>
                      {/* Bouton supprimer — affiché uniquement si autorisé */}
                      {canDelete(user) && (
                        <button
                          onClick={() => handleDelete(user.user_id, displayName)}
                          disabled={isDeleting}
                          style={{
                            padding: "4px 10px",
                            fontSize: 11,
                            fontWeight: 600,
                            fontFamily: font,
                            border: "1px solid #FCA5A5",
                            borderRadius: 6,
                            background: isDeleting ? "#FEE2E2" : "transparent",
                            color: "#DC2626",
                            cursor: isDeleting ? "not-allowed" : "pointer",
                            opacity: isDeleting ? 0.6 : 1,
                          }}
                        >
                          {isDeleting ? "…" : "Supprimer"}
                        </button>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          );
        })}

        {/* Liste vide */}
        {users.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: C.slate, fontSize: 13 }}>
            Aucun utilisateur trouvé
          </div>
        )}
      </div>
    </div>
  );
}